/**
 * Flashcard Session Service
 *
 * Handles per-book word extraction for flashcard sessions.
 * Words are extracted from a book's digital pages, cross-referenced
 * against turn_color_words for turn codes and skill IDs, then
 * filtered by the child's active skill IDs.
 */

import { supabase } from '@/lib/supabase/client';
import type { FlashcardWord } from './flashcardSessionLogic';

/**
 * Get flashcard-eligible words for a specific book, filtered by active skill IDs.
 *
 * Flow:
 * 1. Fetch digital_pages for the book → extract unique words
 * 2. Batch lookup against turn_color_words to get word IDs, turn codes, skill IDs
 * 3. Filter to words in the lexicon AND matching active skills
 * 4. Deduplicate by clean word
 */
export async function getFlashcardWordsForBook(
  bookId: string,
  activeSkillIds: string[]
): Promise<FlashcardWord[]> {
  if (!activeSkillIds.length) return [];

  const { data: pages, error: pagesError } = await supabase
    .from('digital_pages')
    .select('sentence_text')
    .eq('book_id', bookId)
    .order('digital_page_number', { ascending: true });

  if (pagesError || !pages?.length) return [];

  const wordSet = new Set<string>();
  for (const page of pages) {
    if (!page.sentence_text) continue;
    const tokens = page.sentence_text.split(/\s+/);
    for (const token of tokens) {
      const clean = token.toLowerCase().replace(/[^a-z'-]/g, '');
      if (clean.length > 0) wordSet.add(clean);
    }
  }
  const uniqueWords = [...wordSet];
  if (!uniqueWords.length) return [];

  const { data: lexiconMatches, error: lexError } = await supabase
    .from('turn_color_words')
    .select('id, word, turn_code, skill_id')
    .in('word', uniqueWords);

  if (lexError || !lexiconMatches?.length) return [];

  const skillSet = new Set(activeSkillIds);
  const seen = new Set<string>();
  const result: FlashcardWord[] = [];

  for (const m of lexiconMatches) {
    if (!m.skill_id || !skillSet.has(m.skill_id) || seen.has(m.word)) continue;
    seen.add(m.word);
    result.push({
      wordId: m.id,
      word: m.word,
      cleanWord: m.word.toLowerCase().replace(/[^a-z'-]/g, ''),
      turnCode: m.turn_code,
      skillId: m.skill_id,
    });
  }

  return result;
}

/**
 * Check if a book has enough flashcard words for a session.
 */
export async function hasEnoughFlashcardWords(
  bookId: string,
  activeSkillIds: string[],
  minWords: number = 3
): Promise<boolean> {
  const words = await getFlashcardWordsForBook(bookId, activeSkillIds);
  return words.length >= minWords;
}
